import { useCallback, useState } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import dayjs from 'dayjs';
import { useAppToast } from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { fetchPatientDayVisits } from '@/services/main/visitSchedule';
import type { VisitDraftPayload, VisitScheduleEvent } from '../types';
import { MSG } from '../VisitSchedulePage.constants';
import {
  payloadToCalendarVisit,
  removeCalendarVisitAndAdjustGroupCount,
  visitToEvent,
} from '../VisitSchedulePage.helpers';
import { getSamePatientSameDayDuplicateType } from './VisitScheduleDuplicateChecks';
import type { VisitScheduleVisitDisplay } from './VisitScheduleWeekCalendar';

type UseVisitSchedulePageDraftActionsParams = {
  copyCheckFlg: boolean;
  tenant: string;
  clinicNumber: string;
  serviceType: string;
  massagerId: number | null;
  clinicLocationId: number | null;
  calendarVisits: VisitScheduleVisitDisplay[];
  setCalendarVisits: Dispatch<SetStateAction<VisitScheduleVisitDisplay[]>>;
  setDeletedIds: Dispatch<SetStateAction<number[]>>;
  setCopyCheckFlg: Dispatch<SetStateAction<boolean>>;
  hasLocalChangesRef: MutableRefObject<boolean>;
};

export function useVisitSchedulePageDraftActions({
  copyCheckFlg,
  tenant,
  clinicNumber,
  serviceType,
  massagerId,
  clinicLocationId,
  calendarVisits,
  setCalendarVisits,
  setDeletedIds,
  setCopyCheckFlg,
  hasLocalChangesRef,
}: UseVisitSchedulePageDraftActionsParams) {
  const toast = useAppToast();
  const appModal = useAppModal();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedVisit, setSelectedVisit] = useState<VisitScheduleEvent | null>(null);
  const [initialDate, setInitialDate] = useState<string | undefined>();
  const [initialTime, setInitialTime] = useState<string | undefined>();

  const handleCellClick = useCallback(
    (dateStr: string, timeStr: string) => {
      if (copyCheckFlg) {
        appModal.warning({
          content: MSG.add_blocked_by_copy,
        });
        return;
      }
      setInitialDate(dateStr);
      setInitialTime(timeStr);
      setSelectedVisit(null);
      setModalMode('create');
      setModalOpen(true);
    },
    [copyCheckFlg, appModal]
  );

  const handleVisitClick = useCallback(
    (visit: VisitScheduleVisitDisplay) => {
      if (copyCheckFlg) {
        appModal.warning({
          content: MSG.edit_blocked_by_copy,
        });
        return;
      }
      setSelectedVisit(visitToEvent(visit));
      setInitialDate(undefined);
      setInitialTime(undefined);
      setModalMode('edit');
      setModalOpen(true);
    },
    [copyCheckFlg, appModal]
  );

  const handleSave = useCallback(
    (rawPayload: VisitDraftPayload) => {
      // Always stamp the dropdown's currently-selected clinic_location onto the draft
      // so distance computation on submit uses the location's address (BE falls back
      // to massager.address when this is null).
      const payload: VisitDraftPayload = {
        ...rawPayload,
        clinic_location_id: clinicLocationId,
      };
      const copyDates = payload.copy_target_dates ?? [];
      const mainDate = payload.visit_datetime_from
        ? dayjs(payload.visit_datetime_from).format('YYYY-MM-DD')
        : initialDate;
      const timeStr = payload.visit_datetime_from
        ? dayjs(payload.visit_datetime_from).format('HH:mm')
        : (initialTime ?? '09:00');
      const mid = massagerId ?? 0;

      hasLocalChangesRef.current = true;
      if (modalMode === 'create') {
        const toAdd: VisitScheduleVisitDisplay[] = [];
        if (!payload.copy_only && mainDate) {
          toAdd.push(payloadToCalendarVisit(payload, mainDate, timeStr, mid));
        }
        copyDates.forEach(d => {
          if (d && d !== mainDate)
            toAdd.push(payloadToCalendarVisit(payload, d, timeStr, mid, { isCopied: true }));
        });
        if (toAdd.length === 0 && mainDate)
          toAdd.push(payloadToCalendarVisit(payload, mainDate, timeStr, mid));

        const pid = payload.patient_id ?? 0;
        const existingByDate = new Set(
          calendarVisits
            .filter(v => v.patient_id === pid && (v.massager_id ?? 0) === mid)
            .map(v => dayjs(v.visit_datetime_from).format('YYYY-MM-DD'))
        );
        const filtered = toAdd.filter(v => {
          const d = dayjs(v.visit_datetime_from).format('YYYY-MM-DD');
          if (existingByDate.has(d)) return false;
          existingByDate.add(d);
          return true;
        });
        setCalendarVisits(prev => [...prev, ...filtered]);
      } else if (selectedVisit) {
        const updatedDate = dayjs(
          payload.visit_datetime_from || selectedVisit.visit_datetime_from
        ).format('YYYY-MM-DD');
        const updatedTime = dayjs(
          payload.visit_datetime_from || selectedVisit.visit_datetime_from
        ).format('HH:mm');
        const updated = payloadToCalendarVisit(payload, updatedDate, updatedTime, mid);
        Object.assign(updated, {
          id: selectedVisit.id,
          patient_base_address1:
            updated.patient_base_address1 ?? selectedVisit.patient_base_address1,
          patient_base_address2:
            updated.patient_base_address2 ?? selectedVisit.patient_base_address2,
          patient_base_address3:
            updated.patient_base_address3 ?? selectedVisit.patient_base_address3,
          patient_base_address_name:
            updated.patient_base_address_name ?? selectedVisit.patient_base_address_name,
        });

        // Copies on other weekdays also apply in edit mode — add a new draft per target date,
        // skipping dates that already have a visit for this patient+massager.
        const pid = payload.patient_id ?? 0;
        const existingByDate = new Set(
          calendarVisits
            .filter(
              v => v.patient_id === pid && (v.massager_id ?? 0) === mid && v.id !== selectedVisit.id
            )
            .map(v => dayjs(v.visit_datetime_from).format('YYYY-MM-DD'))
        );
        existingByDate.add(updatedDate);
        const toAdd: VisitScheduleVisitDisplay[] = [];
        copyDates.forEach(d => {
          if (!d || d === updatedDate) return;
          if (existingByDate.has(d)) return;
          existingByDate.add(d);
          toAdd.push({
            ...payloadToCalendarVisit(payload, d, updatedTime, mid, { isCopied: true }),
            copy_visit_id: typeof selectedVisit.id === 'number' ? selectedVisit.id : undefined,
          });
        });

        setCalendarVisits(prev => [
          ...prev.map(v => (v.id === selectedVisit.id ? updated : v)),
          ...toAdd,
        ]);
      }
      setModalOpen(false);
    },
    [
      modalMode,
      selectedVisit,
      initialDate,
      initialTime,
      massagerId,
      clinicLocationId,
      calendarVisits,
      hasLocalChangesRef,
      setCalendarVisits,
    ]
  );

  const handleCopyToOtherDays = useCallback(
    (rawPayload: VisitDraftPayload, dates: string[]) => {
      const uniqueDates = Array.from(new Set(dates)).filter(Boolean);
      if (uniqueDates.length === 0) {
        setModalOpen(false);
        return;
      }
      const payload: VisitDraftPayload = {
        ...rawPayload,
        clinic_location_id: clinicLocationId,
      };
      const timeStr = payload.visit_datetime_from
        ? dayjs(payload.visit_datetime_from).format('HH:mm')
        : (initialTime ?? '09:00');
      const mid = massagerId ?? 0;
      hasLocalChangesRef.current = true;
      const toAdd = uniqueDates.map(d => ({
        ...payloadToCalendarVisit(payload, d, timeStr, mid, { isCopied: true }),
        copy_visit_id: typeof selectedVisit?.id === 'number' ? selectedVisit.id : undefined,
      }));
      const pid = payload.patient_id ?? 0;
      const existingByDate = new Set(
        calendarVisits
          .filter(v => v.patient_id === pid && (v.massager_id ?? 0) === mid)
          .map(v => dayjs(v.visit_datetime_from).format('YYYY-MM-DD'))
      );
      const filtered = toAdd.filter(v => {
        const d = dayjs(v.visit_datetime_from).format('YYYY-MM-DD');
        if (existingByDate.has(d)) return false;
        existingByDate.add(d);
        return true;
      });
      setCalendarVisits(prev => [...prev, ...filtered]);
      setCopyCheckFlg(true);
      setModalOpen(false);
    },
    [
      initialTime,
      massagerId,
      clinicLocationId,
      calendarVisits,
      hasLocalChangesRef,
      setCalendarVisits,
      setCopyCheckFlg,
    ]
  );

  const handleDelete = useCallback(
    (id: string | number) => {
      if (copyCheckFlg) {
        toast.error('コピー元のため削除できません。一度データ更新してください。');
        return;
      }
      hasLocalChangesRef.current = true;
      if (typeof id === 'number') {
        setDeletedIds(prev => [...prev, id]);
      }
      setCalendarVisits(prev => removeCalendarVisitAndAdjustGroupCount(prev, id));
      setModalOpen(false);
      setSelectedVisit(null);
    },
    [copyCheckFlg, toast, hasLocalChangesRef, setDeletedIds, setCalendarVisits]
  );

  const handleVisitDrop = useCallback(
    async (visit: VisitScheduleVisitDisplay, dateStr: string, timeStr: string) => {
      // RA2 parity (schedule.blade.php:924-962): cross-date -> locked -> copy -> 突発
      const nextFrom = dayjs(`${dateStr}T${timeStr}`);
      const originalFrom = dayjs(visit.visit_datetime_from);
      const durationMinutes = dayjs(visit.visit_datetime_to).diff(originalFrom, 'minute');
      const nextTo = nextFrom.add(durationMinutes, 'minute');

      // 1. Cross-date -> silent revert (RA2: startday != endday)
      if (nextFrom.format('YYYY-MM-DD') !== nextTo.format('YYYY-MM-DD')) {
        return;
      }
      // 2. Locked
      if (visit.is_locked) {
        appModal.warning({
          content: 'ロックされているため、編集及び削除はできません。',
        });
        return;
      }
      // 3. Pending copy data
      if (copyCheckFlg) {
        appModal.warning({
          content: 'コピーデータが存在するため移動できません。一度データ更新してください。',
        });
        return;
      }
      // 4. 突発的な往療
      if (visit.is_suddenly_visit) {
        appModal.warning({
          content: '突発的な往療にチェックが入っているためドラッグアンドドロップでは移動できません',
        });
        return;
      }

      // No-op drop (same slot)
      if (
        originalFrom.format('YYYY-MM-DDTHH:mm:ss') === nextFrom.format('YYYY-MM-DDTHH:mm:ss') &&
        dayjs(visit.visit_datetime_to).format('YYYY-MM-DDTHH:mm:ss') ===
          nextTo.format('YYYY-MM-DDTHH:mm:ss')
      ) {
        return;
      }

      const localEvents = calendarVisits.map(visitToEvent);
      let remoteEvents: VisitScheduleEvent[] = [];
      if (tenant && clinicNumber && visit.patient_id > 0) {
        try {
          const rows = await fetchPatientDayVisits(
            tenant,
            clinicNumber,
            visit.patient_id,
            dateStr,
            undefined,
            serviceType
          );
          remoteEvents = rows.map(row => ({
            id: row.id,
            patient_id: row.patient_id,
            massager_id: row.massager_id,
            visit_datetime_from: row.visit_datetime_from,
            visit_datetime_to: row.visit_datetime_from,
            visit_minutes: 0,
            is_draft: false,
            is_locked: row.is_locked,
            is_suddenly_visit: row.is_suddenly_visit,
          }));
        } catch {
          // API error: keep legacy local-only drag/drop behavior.
        }
      }
      const localIds = new Set(localEvents.map(event => event.id));
      const duplicateType = getSamePatientSameDayDuplicateType({
        events: [
          ...localEvents,
          ...remoteEvents.filter(event => !localIds.has(event.id)),
        ],
        editingId: visit.id,
        patientId: visit.patient_id,
        currentMassagerId: visit.massager_id,
        visitDate: dateStr,
      });
      if (duplicateType === 'same_massager') {
        appModal.warning({ content: 'この受診者はすでに登録されています。' });
        return;
      }
      if (duplicateType === 'other_massager') {
        appModal.warning({ content: 'この受診者はすでに他の施術師にて登録されています。' });
        return;
      }

      hasLocalChangesRef.current = true;
      setCalendarVisits(prev =>
        prev.map(item =>
          item.id === visit.id
            ? {
                ...item,
                visit_datetime_from: nextFrom.format('YYYY-MM-DDTHH:mm:ss'),
                visit_datetime_to: nextTo.format('YYYY-MM-DDTHH:mm:ss'),
                visit_minutes: durationMinutes,
              }
            : item
        )
      );
    },
    [
      copyCheckFlg,
      appModal,
      calendarVisits,
      tenant,
      clinicNumber,
      serviceType,
      hasLocalChangesRef,
      setCalendarVisits,
    ]
  );

  /** Resize a visit by dragging its bottom edge — updates visit_minutes + visit_datetime_to. */
  const handleVisitResize = useCallback(
    (visit: VisitScheduleVisitDisplay, newMinutes: number) => {
      // RA2 parity (schedule.blade.php:894-914): cross-date -> locked -> copy (no 突発 check)
      if (newMinutes <= 0) return;

      const from = dayjs(visit.visit_datetime_from);
      const nextTo = from.add(newMinutes, 'minute');

      // 1. Cross-date
      if (from.format('YYYY-MM-DD') !== nextTo.format('YYYY-MM-DD')) {
        appModal.warning({
          content: '日付をまたいだ施術日は登録できません',
        });
        return;
      }
      // 2. Locked
      if (visit.is_locked) {
        appModal.warning({
          content: 'ロックされているため、編集及び削除はできません。',
        });
        return;
      }
      // 3. Pending copy data
      if (copyCheckFlg) {
        appModal.warning({
          content: 'コピーデータが存在するため変更できません。一度データ更新してください。',
        });
        return;
      }

      hasLocalChangesRef.current = true;
      setCalendarVisits(prev =>
        prev.map(item =>
          item.id === visit.id
            ? {
                ...item,
                visit_datetime_to: nextTo.format('YYYY-MM-DDTHH:mm:ss'),
                visit_minutes: newMinutes,
              }
            : item
        )
      );
    },
    [copyCheckFlg, appModal, hasLocalChangesRef, setCalendarVisits]
  );

  return {
    modalOpen,
    setModalOpen,
    selectedVisit,
    initialDate,
    initialTime,
    handleCellClick,
    handleVisitClick,
    handleSave,
    handleCopyToOtherDays,
    handleDelete,
    handleVisitDrop,
    handleVisitResize,
  };
}
